export type PartnerRelation = 'customer-user' | 'program-award' | 'investor' | 'collaborator-project';

export interface Partner {
    name: string;
    src: string;
    url?: string;
    order?: number;
    relation?: PartnerRelation[];
}

export const Partners: Partner[] = [
    { name: "NHS CEP", src: "/images/partners/nhs-cep.webp", url: "#", relation: ['program-award', 'collaborator-project'] },
    { name: "Harvard i-lab", src: "/images/partners/harvard-i-lab.webp", url: "#", relation: ['program-award'] },
    { name: "The Guardian", src: "/images/partners/guardian.webp", relation: ['program-award'] },
    { name: "UKABIF", src: "/images/partners/ukabif.webp", relation: ['collaborator-project'] },
    { name: "NIHR HRC", src: "/images/partners/nihr-hrc.webp", relation: ['collaborator-project'] },
    { name: "St George's NHS", src: "/images/partners/st-georges-nhs.png", relation: ['customer-user', 'collaborator-project'] },
    { name: "BGV", src: "/images/partners/bgv.webp", relation: ['investor'] },
    { name: "UH Cleveland", src: "/images/partners/uh-cleveland.webp", url: "#", relation: ['customer-user'] },
    { name: "Cognivate", src: "/images/partners/cognivate.webp", relation: ['customer-user'] },
    { name: "Health Foundation", src: "/images/partners/health-foundation.webp", relation: ['program-award', 'investor'] },
    { name: "University of Glasgow", src: "/images/partners/uog.webp", relation: ['collaborator-project'] },
    { name: "Linguistic Resolutions", src: "/images/partners/linguistic-resolutions.webp", relation: ['customer-user'] },

];

export type UserType = 'professional' | 'patient' | 'carer';

export interface Testimonial {
    quote: string;
    name: string;
    role: string;
    organization?: string;
    organizationLogo?: string; // e.g., '/images/partners/logo.webp'
    image?: string;
    userType?: UserType[];
    order?: number;
}

export const Testimonials: Testimonial[] = [
    {
        quote: "Not only does it help people to remember and carry out their plans, it allows their family, carers and health team to support them and follow their progress - helping my clients increase their independence, their confidence, and their sense of competence.",
        name: "Dr. Jill Winegardner",
        role: "Director of Neuropsychological Rehabilitation",
        organization: "UH Cleveland",
        image: "/images/testimonials/jill-winegardner.png",
        userType: ['professional'],
        order: 1
    },
    {
        quote: "Our client has really enjoying the app and it has significantly helped his sleep schedule. I have another client who we're trialling with at the moment who is typically quite resistant to prompting and even he is engaging really well with it.",
        name: "Giulia",
        role: "Assistant Psychologist",
        organization: "UH Cleveland",
        userType: ['professional'],
        order: 2
    },
    {
        quote: "Invaluable for organising my day with regular tasks. The prompts are really useful and I gleefully tick off tasks as I complete them and the feeling is marvellous.",
        name: "Antoinette",
        role: "Survivor",
        image: "/images/testimonials/antoinette.png",
        userType: ['patient'],
        order: 3
    },
    {
        quote: "The app is a lifesaver! I use neumind for reminders about medication and hospital appointments. But I also try to balance it out with fun things as well to remind me to meet friends for a coffee.",
        name: "Calhan",
        role: "Brain Injury Survivor",
        image: "/images/testimonials/calhan.png",
        userType: ['patient'],
        order: 4
    },
    {
        quote: "I would like to thank you for doing this. I think it’s really important when you’ve had a brain injury - it’s an invisible disability - it’s important to feel that you’ve got a voice. The neumind app has helped me to get my visibility back, through the community.",
        name: "Sian",
        role: "TBI",
        image: "",
        userType: ['patient'],
        order: 5
    },
    {
        quote: "I find it easier and more reliable than my own phone diary. I would recommend to anyone within the brain injury community as it is a very useful application.",
        name: "Hannah",
        role: "Brain Injury",
        image: "/images/testimonials/hannah.png",
        userType: ['patient'],
        order: 6
    },
    {
        quote: "The app is helping my wife Debbie take the initiative to develop new habits.",
        name: "Stephen",
        role: "Husband",
        image: "/images/testimonials/stephen.png",
        userType: ['carer'],
        order: 7
    },
    {
        quote: "The app is a lifesaver! I use neumind for reminders about medication and hospital appointments. But I also try to balance it out with fun things as well to remind me to meet friends for a coffee. I think that’s quite important as well.",
        name: "Sean",
        role: "Brain Injury Survivor",
        image: "",
        userType: ['patient'],
        order: 8
    }

];

export interface Metric {
    id: string;
    value: string;
    label: string;
    tagline?: string;
    description?: string;
}

export const Metrics: Record<string, Metric> = {
    taskAdherence: {
        id: 'taskAdherence',
        tagline: "Double Engagement",
        value: "83%",
        label: "Therapy task adherence",
        description: "More than double current standards"
    },
    timeSaving: {
        id: 'timeSaving',
        tagline: "Reduce Admin",
        value: "50%",
        label: "Time saving",
        description: "From session planning and reporting"
    },
    independence: {
        id: 'independence',
        tagline: "Improve Outcomes",
        value: "85%",
        label: "Improved independence",
        description: "Reported by clients after 2 weeks"
    },
    promptsDelivered: {
        id: 'promptsDelivered',
        value: "162,338",
        label: "Prompts delivered",
    },
    journalEntries: {
        id: 'journalEntries',
        value: "9,571",
        label: "Journals / tracker entries",
    },
    educationModules: {
        id: 'educationModules',
        value: "3,545",
        label: "Education modules completed",
    }
};

export interface ProofBarItem {
    icon: string;
    label: string;
    text: string;
    highlight: string;
    link?: {
        label: string;
        href: string;
    };
}

export const ProofBarData: Record<string, ProofBarItem[]> = {
    professionals: [
        {
            icon: 'schedule',
            label: 'Reduce Admin',
            highlight: '50% Less Admin',
            text: 'Halve your planning time with AI-assisted tools'
        },
        {
            icon: 'trending_up',
            label: 'Increase Engagement',
            highlight: '2x Adherence',
            text: 'Double the engagement of paper-based therapy'
        },
        {
            icon: 'account_balance_wallet',
            label: 'Flexible Funding',
            highlight: '3 Pathways',
            text: 'Flexible billing for clinics, insurers, or patients',
            link: {
                label: 'View Enterprise Options',
                href: '#enterprise-section'
            }
        }
    ],
    individuals: [
        {
            icon: 'accessibility_new',
            label: 'Gain Independence',
            highlight: '85% of Users',
            text: 'Feel more independent after just 2 weeks'
        },
        {
            icon: 'check_circle',
            label: 'Take Positive Steps',
            highlight: Metrics.promptsDelivered.value,
            text: 'Prompts delivered on neumind'
        },
        {
            icon: 'group',
            label: 'Share Your Journey',
            highlight: 'Family Included',
            text: 'Invite loved ones to support your journey'
        }
    ],
    complete: [
        {
            icon: 'savings',
            label: 'Justify Care',
            highlight: 'Justify Spend',
            text: 'Secure funding with objective outcome data'
        },
        {
            icon: 'insights',
            label: 'Evidenced Outcomes',
            highlight: 'Track Progress',
            text: 'Monitor client engagement between visits'
        },
        {
            icon: 'receipt_long',
            label: 'Simple Billing',
            highlight: 'One Invoice',
            text: 'One annual invoice covers all software and support.'
        }
    ]
};


